import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Send, Star, MessageSquarePlus } from "lucide-react";
import { MaskTextLoad, FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-ink/20 py-3.5 font-sans text-base md:text-lg text-ink placeholder-ink/35 focus:outline-none focus:border-magenta transition-colors duration-300";

function ReviewCard({ r, i }) {
  return (
    <StaggerItem className="h-full">
      <div
        data-testid={`review-card-${r.id}`}
        className="group relative h-full rounded-3xl border border-ink/10 bg-white p-7 md:p-8 card-shadow overflow-hidden"
      >
        <div className="absolute -top-6 -right-4 h-24 w-24 rounded-full brand-gradient blur-[60px] opacity-20 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none" />
        <Quote size={30} className="text-coral/70" />
        <p className="mt-4 font-serif italic font-light text-lg md:text-xl leading-relaxed text-ink/85">
          “{r.experience}”
        </p>
        <div className="mt-6 flex items-center gap-3">
          <div className="h-11 w-11 shrink-0 rounded-full brand-gradient flex items-center justify-center font-display text-sm font-bold text-white">
            {r.first_name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <p className="font-display text-sm font-bold text-ink">{r.first_name}</p>
            <p className="font-sans text-xs text-ink/50">{r.institution}</p>
          </div>
        </div>
      </div>
    </StaggerItem>
  );
}

function EditionSection({ edition, reviews, onSubmitted }) {
  const [form, setForm] = useState({ first_name: "", institution: "", experience: "" });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.institution.trim() || !form.experience.trim()) {
      toast.error("Please fill in your name, school and experience.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, { edition_id: edition.id, ...form });
      onSubmitted(data);
      setForm({ first_name: "", institution: "", experience: "" });
      setOpen(false);
      toast.success("Thank you for sharing your experience!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 md:py-24 border-t border-ink/10 first:border-t-0" data-testid={`edition-section-${edition.id}`}>
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <FadeUp>
          <div className="flex items-baseline gap-4 flex-wrap">
            <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta">{edition.name}</p>
            <span className="rounded-full bg-coral/10 px-3 py-1 font-display text-[0.65rem] uppercase tracking-[0.12em] text-coral font-bold">
              {reviews.length} {reviews.length === 1 ? "voice" : "voices"}
            </span>
          </div>
          <h2 className="mt-3 font-serif font-light text-4xl md:text-5xl text-ink leading-[0.95]">
            {edition.subtitle || "What they lived."}
          </h2>
        </FadeUp>

        {reviews.length > 0 ? (
          <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" stagger={0.06}>
            {reviews.map((r, i) => (
              <ReviewCard key={r.id} r={r} i={i} />
            ))}
          </Stagger>
        ) : (
          <FadeUp>
            <div className="mt-12 rounded-3xl border border-dashed border-ink/15 bg-white/40 p-12 text-center">
              <Star size={26} className="mx-auto text-coral/60" />
              <p className="mt-4 font-serif italic text-xl text-ink/50">No stories yet — be the first to share.</p>
            </div>
          </FadeUp>
        )}

        {/* Submission */}
        <div className="mt-10">
          {!open ? (
            <button
              onClick={() => setOpen(true)}
              data-testid={`open-review-form-${edition.id}`}
              className="group inline-flex items-center gap-3 rounded-full brand-gradient px-8 py-4 font-display text-sm uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(232,38,111,0.35)]"
            >
              <MessageSquarePlus size={18} /> Share your experience
            </button>
          ) : (
            <AnimatePresence>
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={submit}
                data-testid={`review-form-${edition.id}`}
                className="rounded-3xl border border-ink/10 bg-white card-shadow p-7 md:p-10 max-w-2xl"
              >
                <p className="font-display text-xs uppercase tracking-[0.25em] text-magenta">Share your story — {edition.name}</p>
                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div>
                    <label className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">First name</label>
                    <input data-testid={`review-first-name-${edition.id}`} className={inputCls} placeholder="Your first name" value={form.first_name} onChange={update("first_name")} />
                  </div>
                  <div>
                    <label className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">School / Institution</label>
                    <input data-testid={`review-institution-${edition.id}`} className={inputCls} placeholder="Where you're from" value={form.institution} onChange={update("institution")} />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="font-display text-xs uppercase tracking-[0.18em] text-ink/40">Your experience</label>
                  <textarea data-testid={`review-experience-${edition.id}`} rows={4} className={`${inputCls} resize-none`} placeholder="Tell us what this edition meant to you..." value={form.experience} onChange={update("experience")} />
                </div>
                <div className="mt-8 flex items-center gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid={`submit-review-${edition.id}`}
                    className="group inline-flex items-center gap-3 rounded-full brand-gradient px-8 py-3.5 font-display text-sm uppercase tracking-[0.14em] font-bold text-white transition-all duration-300 hover:scale-[1.03] disabled:opacity-60"
                  >
                    {loading ? "Sharing..." : "Submit review"}
                    <Send size={15} className="transition-transform group-hover:translate-x-1" />
                  </button>
                  <button type="button" onClick={() => setOpen(false)} className="font-display text-xs uppercase tracking-[0.14em] text-ink/40 hover:text-ink transition-colors">
                    Cancel
                  </button>
                </div>
              </motion.form>
            </AnimatePresence>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Reviews() {
  const [editions, setEditions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ed, rv] = await Promise.all([axios.get(`${API}/editions`), axios.get(`${API}/reviews`)]);
        setEditions(ed.data);
        setReviews(rv.data);
      } catch {
        toast.error("Could not load reviews.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addReview = (r) => setReviews((prev) => [r, ...prev]);

  return (
    <div className="bg-cream min-h-screen" data-testid="reviews-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-40 pb-16 md:pb-24">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full brand-gradient blur-[140px] opacity-20 pointer-events-none" />
        <div className="absolute top-10 right-0 h-80 w-80 rounded-full bg-coral blur-[140px] opacity-15 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8">
          <p className="font-display text-xs uppercase tracking-[0.3em] text-magenta mb-6">Event Experience Reviews</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl leading-[0.9] text-ink max-w-3xl">
            <MaskTextLoad lines={["In their own", "words."]} />
          </h1>
          <FadeUp delay={0.4}>
            <p className="mt-8 font-sans text-base md:text-lg text-ink/60 leading-relaxed max-w-xl">
              Every gathering leaves a mark. These are the voices of young women who came, who listened, and who left changed — edition by edition.
            </p>
          </FadeUp>
        </div>
      </section>

      {loading ? (
        <div className="py-24 text-center font-serif italic text-xl text-ink/40">Gathering the voices…</div>
      ) : (
        <div className="pb-10">
          {editions.map((ed) => (
            <EditionSection
              key={ed.id}
              edition={ed}
              reviews={reviews.filter((r) => r.edition_id === ed.id)}
              onSubmitted={addReview}
            />
          ))}
        </div>
      )}
    </div>
  );
}
